import { splitHighlight } from '../lib/search.ts';
import { atomsOf, splitSelection, type Span } from '../lib/selection.ts';
import { smileyUrl } from '../lib/smileys.ts';

/**
 * Text with its `:code:` smileys turned back into pictures and its addresses
 * turned into links.
 *
 * Its own file because three things render a message's words now — a text
 * bubble, a reply quote and an album's caption — and the third would otherwise
 * have had to import the thread that draws it.
 *
 * Addresses are found first and the codes parsed inside what is left, so an
 * address that happens to contain something code-shaped stays whole. That
 * splitting is `atomsOf`, which also hands back where each piece started in the
 * body — the number that goes out as `data-at` and lets a caret dropped
 * anywhere on screen be read back as an index into the stored string.
 *
 * `highlight` is what the search field holds, and it cuts the plain runs one
 * step further. Last of the three on purpose: by then the codes have already
 * become pictures, so a query cannot mark the letters inside `:wink:` — letters
 * the reader is not being shown — and split the codec in half doing it.
 *
 * `selection` is the fragment somebody is pulling handles around. Drawn here
 * rather than by the browser because a bubble is not text: see lib/selection.ts.
 */
export function TextBody({
  body,
  links = true,
  highlight,
  selection,
}: {
  body: string;
  links?: boolean;
  highlight?: string;
  selection?: Span | null;
}) {
  const span = selection ?? null;

  /** The searched-for words inside one piece of a run, marked where they hit. */
  const marked = (value: string, key: string) =>
    highlight
      ? splitHighlight(value, highlight).map((piece, index) =>
          piece.hit ? (
            <mark key={`${key}-${index}`} className="text-hit">
              {piece.value}
            </mark>
          ) : (
            piece.value
          ),
        )
      : value;

  return (
    <>
      {atomsOf(body, links).map((atom, index) => {
        if (atom.type === 'link') {
          return (
            <a
              key={index}
              className={`message-link${
                span && span.start <= atom.at && span.end >= atom.at + atom.value.length
                  ? ' message-picked'
                  : ''
              }`}
              data-at={atom.at}
              data-len={atom.value.length}
              href={atom.href}
              target="_blank"
              // noreferrer implies noopener, but both are spelled out: the pair is
              // what stops the opened page from reaching back through window.opener.
              rel="noreferrer noopener"
              // The bubble under it opens a menu on a hold and the thread swipes to
              // reply; a tap that landed on the link is only ever the link's.
              onClick={(event) => event.stopPropagation()}
            >
              {atom.value}
            </a>
          );
        }

        if (atom.type === 'smiley') {
          return (
            <img
              key={index}
              className={`message-smiley${
                span && span.start <= atom.at && span.end >= atom.at + atom.slug.length + 2
                  ? ' message-picked'
                  : ''
              }`}
              data-at={atom.at}
              data-len={atom.slug.length + 2}
              src={smileyUrl(atom.slug)}
              alt={atom.label}
              title={atom.label}
            />
          );
        }

        // The run carries the message's own line breaks, and wears the pre-wrap
        // that keeps them. On the run rather than on the bubble: pre-wrap there
        // would preserve the whitespace around a smiley image too, which the
        // handoff notes shows up as extra height.
        return (
          <span key={index} className="message-text" data-at={atom.at} data-len={atom.value.length}>
            {span
              ? splitSelection(atom.value, atom.at, span).map((piece, pieceIndex) =>
                  piece.on ? (
                    <span key={pieceIndex} className="message-picked">
                      {marked(piece.value, `${index}-${pieceIndex}`)}
                    </span>
                  ) : (
                    marked(piece.value, `${index}-${pieceIndex}`)
                  ),
                )
              : marked(atom.value, String(index))}
          </span>
        );
      })}
    </>
  );
}
